import { FC, useMemo } from "react";

import {
  CreateGUFAddRealisedInstallationTask,
  EditGUFAddRealisedInstallationTask,
} from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GufAddRealisedInstallationFormProps } from "@/types/form-types";

import { getGufAddRealisedInstallationSchema } from "../schema/guf-add-realised-installation-schema";
import { useGufAddRealisedInstallationUiSchema } from "../schema/guf-add-realised-installation-ui-schema";
import { validateGufAddRealisedInstallationForm } from "../schema/guf-validation";

const GufAddRealisedInstallationForm: FC<GufAddRealisedInstallationFormProps> = (props) => {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();

  const { oldUploadTask, task, type } = getFormObjectValues(props);
  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    CreateGUFAddRealisedInstallationTask | EditGUFAddRealisedInstallationTask
  >({
    key: "guf-add-realised-installation",
    task,
  });

  const uiSchema = useGufAddRealisedInstallationUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(
    () => getGufAddRealisedInstallationSchema({ type, isEditing }),
    [type, isEditing]
  );

  const onSubmit = async () => {
    if (!formData) return;
    if (getIsEditing(formData)) {
      await api.editGUFAddRealisedInstallation(formData);
    } else {
      await api.createGUFAddRealisedInstallation(formData);
    }
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });
    await afterSubmit();
  };

  return (
    <BrostarForm<CreateGUFAddRealisedInstallationTask | EditGUFAddRealisedInstallationTask>
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={uiSchema}
      onSubmit={onSubmit}
      customValidate={validateGufAddRealisedInstallationForm}
    />
  );
};

export { GufAddRealisedInstallationForm };
